<template>
  <div>
    <form @submit.prevent="save" novalidate>
      <div class="modal-card" style="width:900px; max-width:100%">
        <header class="modal-card-head">
          <div class="is-flex-grow-1">
            <p class="modal-card-title has-text-weight-bold">Holiday schedule</p>
            <p class="has-text-grey is-size-7">Let {{ connection.retailer.name }} know which days your warehouse can't fulfill orders.</p>
          </div>
          <button type="button" class="delete" aria-label="close" @click="$parent.close()"></button>
        </header>
        <section class="modal-card-body">
          <!-- General Errors -->
          <div v-if="error" class="mb-md">
            <b-message type="is-danger">{{ error }}</b-message>
          </div>
          <!-- Year -->
          <b-field label="Year">
            <b-select v-model="year" :disabled="saving">
              <option v-for="option in yearOptions" :key="option" :value="option">{{ option }}</option>
            </b-select>
          </b-field>
          <!-- Holidays -->
          <div v-if="holidays.length > 0" class="has-border rounded-lg">
            <table class="table is-fullwidth holiday-schedule-table">
              <thead>
                <tr class="has-background-white-ter is-size-7 is-uppercase">
                  <th width="25%">Date</th>
                  <th>Holiday or closure</th>
                  <th width="1%">Fulfillment</th>
                  <th width="1%"></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(holiday, index) in holidays" :key="holiday.key" :class="{ 'has-background-danger-light': hasErrors(holiday) }">
                  <!-- Date -->
                  <td>
                    <div v-if="!holiday.editingDate" class="holiday-schedule-date">
                      <a href="#" class="has-text-black" title="Change date" @click.prevent="holiday.editingDate = true">{{ $dates(holiday.date).format('MMM D, YYYY') }}</a>
                      <p class="has-text-grey is-size-7">{{ $dates(holiday.date).format('dddd') }}</p>
                    </div>
                    <b-field
                      v-else
                      :type="showErrors(holiday) && dateError(holiday) ? 'is-danger' : ''"
                      :message="showErrors(holiday) ? dateError(holiday) : ''">
                      <b-input
                        v-model="holiday.date"
                        type="date"
                        :min="minDate"
                        :max="maxDate"
                        :disabled="saving" />
                    </b-field>
                  </td>
                  <!-- Name -->
                  <td>
                    <b-field
                      :type="showErrors(holiday) && nameError(holiday) ? 'is-danger' : ''"
                      :message="showErrors(holiday) ? nameError(holiday) : ''">
                      <b-input
                        v-model="holiday.name"
                        placeholder="e.g. Year-end inventory count"
                        maxlength="128"
                        :has-counter="false"
                        :disabled="saving"
                        @blur="holiday.touched = true" />
                    </b-field>
                  </td>
                  <!-- Fulfillment -->
                  <td>
                    <b-field>
                      <b-radio-button v-model="holiday.operating" :native-value="false" type="is-dark" :disabled="saving">Closed</b-radio-button>
                      <b-radio-button v-model="holiday.operating" :native-value="true" type="is-success" :disabled="saving">Operating</b-radio-button>
                    </b-field>
                  </td>
                  <!-- Remove -->
                  <td>
                    <a href="#" class="has-text-grey" title="Remove" @click.prevent="removeHoliday(index)"><b-icon icon="delete-outline" /></a>
                  </td>
                </tr>
              </tbody>
            </table>
            <div class="border-t px-md py-sm">
              <a href="#" class="has-text-info" @click.prevent="addDate"><b-icon icon="plus" size="is-small" /> Add date</a>
            </div>
          </div>
          <!-- Empty State -->
          <div v-else class="holiday-schedule-empty rounded-lg has-text-centered pa-xl">
            <b-icon icon="calendar-minus" size="is-large" custom-class="has-text-grey" />
            <p class="is-size-5 has-text-weight-bold mt-sm">No dates added for {{ year }}</p>
            <p class="has-text-grey mb-lg">
              Add holidays and planned closures so {{ connection.retailer.name }} can plan around them.
              Each date is marked Closed unless you set it to Operating.
            </p>
            <b-button type="is-primary" icon-left="plus" :disabled="saving" @click="addDate">Add date</b-button>
          </div>
        </section>
        <footer class="modal-card-foot space-between">
          <p class="has-text-grey is-size-7">{{ summary }}</p>
          <div class="buttons">
            <b-button @click="$parent.close()">Cancel</b-button>
            <b-button
              type="is-primary"
              native-type="submit"
              :loading="saving"
              :disabled="saving || holidays.length === 0">
              Save schedule
            </b-button>
          </div>
        </footer>
      </div>
    </form>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

export default {
  data () {
    return {
      year: this.$dates().year(),
      holidays: [],
      saving: false,
      submitted: false,
      error: null,
      nextKey: 0
    }
  },
  computed: {
    ...mapGetters(['context']),
    endpoint () {
      return this.context.route + '/connections/' + this.connection.id + '/'
    },
    yearOptions () {
      const currentYear = this.$dates().year()
      const options = [currentYear, currentYear + 1]
      if (this.schedule && !options.includes(this.schedule.year)) options.unshift(this.schedule.year)
      return options
    },
    minDate () {
      return this.year + '-01-01'
    },
    maxDate () {
      return this.year + '-12-31'
    },
    valid () {
      return this.holidays.every(holiday => !this.hasErrors(holiday))
    },
    summary () {
      if (this.holidays.length === 0) return 'Add at least one date to save'
      const operating = this.holidays.filter(holiday => holiday.operating).length
      const closed = this.holidays.length - operating
      return [
        this.holidays.length + (this.holidays.length === 1 ? ' day' : ' days'),
        closed + ' closed',
        operating + ' operating',
        'Shared with ' + this.connection.retailer.name
      ].join(' · ')
    },
    json () {
      return {
        connection_metadata: [{
          name: 'Holiday Schedule',
          type: 'json',
          value: {
            year: this.year,
            holidays: this.holidays
              .map(holiday => ({
                date: holiday.date,
                day: this.$dates(holiday.date).format('dddd'),
                name: holiday.name.trim(),
                operating: holiday.operating
              }))
              .sort((a, b) => a.date.localeCompare(b.date))
          }
        }]
      }
    }
  },
  methods: {
    dateError (holiday) {
      if (!holiday.date) return 'Pick a date'
      if (holiday.date < this.minDate || holiday.date > this.maxDate) return 'Pick a date in ' + this.year
      if (this.holidays.some(other => other !== holiday && other.date === holiday.date)) return 'This date is already listed'
      return ''
    },
    nameError (holiday) {
      return holiday.name.trim() === '' ? 'Enter a name for this date' : ''
    },
    hasErrors (holiday) {
      return this.showErrors(holiday) && (this.dateError(holiday) !== '' || this.nameError(holiday) !== '')
    },
    showErrors (holiday) {
      return this.submitted || holiday.touched
    },
    buildHoliday (holiday = {}, editingDate = false) {
      return {
        key: this.nextKey++,
        date: holiday.date || '',
        name: holiday.name || '',
        operating: holiday.operating === true,
        editingDate: editingDate,
        touched: false
      }
    },
    addDate () {
      this.holidays.push(this.buildHoliday({}, true))
    },
    removeHoliday (index) {
      this.holidays.splice(index, 1)
    },
    save () {
      this.submitted = true
      if (!this.valid) return
      this.saving = true
      this.error = null
      this.$http.patch(this.endpoint, this.json).then(() => {
        this.$emit('saved:connection')
        this.$buefy.toast.open({ type: 'is-success', message: 'Holiday schedule updated!' })
        this.$parent.close()
      }).catch(() => {
        this.error = 'Failed to save holiday schedule. Please try again or contact support.'
      }).finally(() => {
        this.saving = false
      })
    }
  },
  mounted () {
    if (this.schedule) {
      this.year = this.schedule.year
      this.holidays = this.schedule.holidays.map(holiday => this.buildHoliday(holiday))
    }
  },
  props: {
    connection: {
      type: Object,
      required: true
    },
    schedule: {
      type: Object,
      default: null
    }
  }
}
</script>

<style scoped>
.holiday-schedule-empty {
  border: 1px dashed #dbdbdb;
}
/* Bulma's .table:not(:last-child) margin would leave a gap above "Add date" */
.holiday-schedule-table:not(:last-child) {
  margin-bottom: 0;
}
/* top-align cells so an inline error under one input doesn't shift the others */
.holiday-schedule-table td {
  vertical-align: top;
}
.holiday-schedule-date {
  padding-top: calc(0.5em - 1px);
}
</style>
